import { useMemo, useState } from 'react';
import ModelViewer from './ModelViewer';
import { CHARACTER_MODELS, DEFAULT_CHARACTER_MODEL } from '../modelCatalog';
import './CharacterViewerPage.css';

function modelId(path: string) {
  return path.split('/').pop()?.replace(/\.glb$/, '') ?? 'character';
}

function CharacterViewerPage() {
  const [selectedPath, setSelectedPath] = useState(DEFAULT_CHARACTER_MODEL);
  const selected = useMemo(
    () => CHARACTER_MODELS.find((model) => model.path === selectedPath) ?? CHARACTER_MODELS[0],
    [selectedPath],
  );

  return (
    <div className="character-viewer-page">
      <header className="character-topbar">
        <a className="character-menu" href="/" aria-label="Back to lab dashboard">&#9776;</a>
        <div className="character-brand">
          <span className="character-brand-title">CHARACTER LAB</span>
          <span className="character-brand-subtitle">OPEN ASSET VIEWER</span>
        </div>
        <nav className="character-topnav" aria-label="Character lab navigation">
          <a href="/" aria-label="Lab dashboard">Dashboard</a>
          <a href="/viewer" aria-label="Car parts viewer">Car viewer</a>
        </nav>
      </header>

      <main className="character-workspace">
        <aside className="character-panel character-library" aria-label="Character library">
          <div className="character-panel-heading">
            <div>
              <span className="character-eyebrow">LIBRARY</span>
              <h1>Character library</h1>
            </div>
            <span className="character-count">{CHARACTER_MODELS.length} assets</span>
          </div>

          <div className="character-asset-list">
            {CHARACTER_MODELS.map((model) => {
              const active = model.path === selected.path;
              return (
                <button
                  className={`character-asset-card${active ? ' is-selected' : ''}`}
                  key={model.path}
                  onClick={() => setSelectedPath(model.path)}
                  type="button"
                  aria-pressed={active}
                >
                  <span className={`character-asset-avatar avatar-${modelId(model.path)}`} aria-hidden="true">
                    <span>{model.icon}</span>
                  </span>
                  <span className="character-asset-copy">
                    <strong>{model.name}</strong>
                    <span className="character-license-badge">{model.license.split(' + ')[0]}</span>
                  </span>
                  <span className="character-asset-arrow" aria-hidden="true">&#8250;</span>
                </button>
              );
            })}
          </div>

          <a className="character-browse" href="https://github.com/KhronosGroup/glTF-Sample-Assets" target="_blank" rel="noreferrer">
            <span aria-hidden="true">&#9635;</span>
            Browse open assets
            <span aria-hidden="true">&#8599;</span>
          </a>
        </aside>

        <section className="character-stage-column" aria-label="3D character stage">
          <div className="character-stage-heading">
            <div>
              <span className="character-eyebrow">LIVE PREVIEW</span>
              <h2>{selected.name}</h2>
            </div>
            <span className="character-stage-status"><i /> READY</span>
          </div>
          <div className="character-stage">
            <div className="character-stage-toolbar" aria-label="Stage tools">
              <span>DRAG TO ORBIT</span>
              <span className="character-toolbar-divider" aria-hidden="true" />
              <span>SCROLL TO ZOOM</span>
            </div>
            <ModelViewer
              className="character-model-viewer"
              modelPath={selected.path}
              fitSize={2.8}
              groundY={-1.5}
              cameraPosition={[3, 1.8, 5]}
              cameraTarget={[0, -0.4, 0]}
              background="#15181d"
              modelCount={CHARACTER_MODELS.length}
              showGrid
              showContactShadows
            />
            <div className="character-stage-caption">
              <span>{selected.format}</span>
              <span className="character-caption-dot" aria-hidden="true" />
              <span>{selected.rig === 'Yes' ? 'Rigged' : 'Static'}</span>
            </div>
          </div>
          <div className="character-animation-dock" aria-label="Animation area">
            <div>
              <span className="character-eyebrow">ANIMATION</span>
              <strong>{selected.animationCount} clips available</strong>
            </div>
            <span className="character-animation-hint">Select a clip above to preview it on the rig</span>
          </div>
        </section>

        <aside className="character-panel character-details" aria-label="Asset details">
          <div className="character-panel-heading">
            <div>
              <span className="character-eyebrow">SELECTED ASSET</span>
              <h2>Asset details</h2>
            </div>
            <span className="character-detail-icon" aria-hidden="true">*</span>
          </div>

          <dl className="character-specs">
            <div><dt>Name</dt><dd>{selected.name}</dd></div>
            <div><dt>Format</dt><dd>{selected.format}</dd></div>
            <div><dt>Rig</dt><dd>{selected.rig}</dd></div>
            <div><dt>Animations</dt><dd>{selected.animationCount}</dd></div>
            <div><dt>Polygons</dt><dd>{selected.polygons}</dd></div>
            <div><dt>Vertices</dt><dd>{selected.vertices}</dd></div>
            <div><dt>Textures</dt><dd>{selected.textures}</dd></div>
            <div><dt>Materials</dt><dd>{selected.materials}</dd></div>
            <div><dt>File size</dt><dd>{selected.fileSize}</dd></div>
            <div><dt>License</dt><dd><span className="character-license-badge">{selected.license}</span></dd></div>
          </dl>

          <div className="character-detail-actions">
            <a className="character-action character-action-primary" href={selected.path} download>
              <span aria-hidden="true">&#8659;</span> Download GLB
            </a>
            <a className="character-action" href={selected.sourceUrl} target="_blank" rel="noreferrer">
              <span aria-hidden="true">&#8599;</span> View source
            </a>
            <a className="character-action" href={selected.licenseUrl} target="_blank" rel="noreferrer">
              <span aria-hidden="true">i</span> License details
            </a>
          </div>
        </aside>
      </main>
    </div>
  );
}

export default CharacterViewerPage;
